import React, { useEffect, useState } from 'react';
import { Product } from '../types';
import { ShoppingCart, CheckCircle, Truck, ShieldCheck, Phone, Plus, Minus, Leaf, Home, ChevronRight, Heart } from 'lucide-react';
import { CONTACT_INFO } from '../constants';

interface ProductDetailsPageProps {
  product: Product;
  onBack: () => void;
  onAddToCart: (product: Product, quantity: number) => void;
  onToggleWishlist: (product: Product) => void;
  isWishlisted: boolean;
}

export const ProductDetailsPage: React.FC<ProductDetailsPageProps> = ({ 
  product, 
  onBack, 
  onAddToCart,
  onToggleWishlist,
  isWishlisted
}) => {
  const [quantity, setQuantity] = useState(1);
  const basePrice = product.price || 0;
  
  useEffect(() => {
    window.scrollTo(0, 0);
    setQuantity(1);
  }, [product]);

  const handleIncrement = () => {
    setQuantity(prev => prev + 1);
  };

  const handleDecrement = () => {
    setQuantity(prev => (prev > 1 ? prev - 1 : 1));
  };

  const totalPrice = basePrice * quantity;
  const formattedPrice = new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES' }).format(totalPrice);
  const formattedBasePrice = new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES' }).format(basePrice);

  const handleAddToCart = () => {
    onAddToCart(product, quantity);
  };

  return (
    <div className="bg-earth-50 min-h-screen pt-10 pb-20 animate-fade-in">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb Navigation */}
        <nav className="flex flex-wrap items-center text-sm text-gray-600 mb-8" aria-label="Breadcrumb">
          <button 
            onClick={onBack}
            className="flex items-center hover:text-herbal-800 hover:underline transition-colors"
          >
            <Home className="h-4 w-4 mr-1" />
            Home
          </button>
          
          <ChevronRight className="h-4 w-4 mx-2 text-gray-400" />
          
          <span className="text-gray-600 cursor-default">
            {product.category}
          </span>
          
          <ChevronRight className="h-4 w-4 mx-2 text-gray-400" />
          
          <span className="font-bold text-herbal-900 line-clamp-1" aria-current="page">
            {product.name}
          </span>
        </nav>

        <div className="bg-white rounded-3xl shadow-xl border border-earth-100 overflow-hidden">
          <div className="flex flex-col lg:flex-row">
            {/* Image Section */}
            <div className="lg:w-1/2 bg-white p-8 lg:p-12 flex items-center justify-center border-b lg:border-b-0 lg:border-r border-gray-100 bg-gray-50/50 relative">
              <div className="relative w-full h-[400px] lg:h-[500px]">
                <img 
                  src={product.image} 
                  alt={product.name} 
                  className="w-full h-full object-contain filter drop-shadow-md"
                />
              </div>
              {/* Wishlist Heart on Details Page */}
              <button 
                onClick={() => onToggleWishlist(product)}
                className={`absolute top-6 right-6 lg:top-8 lg:right-8 p-3 rounded-full shadow-lg transition-all ${
                  isWishlisted 
                    ? 'bg-turmeric-500 text-white' 
                    : 'bg-white text-gray-400 hover:text-turmeric-500'
                }`}
                title={isWishlisted ? "Remove from wishlist" : "Add to wishlist"}
              >
                <Heart className={`h-6 w-6 ${isWishlisted ? 'fill-current' : ''}`} />
              </button>
            </div>

            {/* Details Section */}
            <div className="lg:w-1/2 p-8 lg:p-16 flex flex-col justify-center">
              <div className="mb-6 flex justify-between items-start">
                <span className="bg-herbal-100 text-herbal-800 text-sm font-bold px-4 py-1.5 rounded-full uppercase tracking-wide inline-block">
                  {product.category}
                </span>
                {basePrice > 0 && (
                   <span className="text-2xl font-bold text-herbal-900">
                     {formattedBasePrice}
                   </span>
                )}
              </div>
              
              <h1 className="font-heading text-4xl sm:text-5xl font-bold text-herbal-900 mb-4 leading-tight">
                {product.name}
              </h1>
              
              <h2 className="text-xl text-turmeric-600 font-semibold mb-8 border-l-4 border-turmeric-500 pl-4">
                {product.purpose}
              </h2>
              
              <div className="prose prose-lg text-gray-600 mb-8 leading-relaxed">
                <p className="text-lg">{product.description}</p>
                
                {product.ingredients && (
                  <div className="mt-6 mb-6">
                    <h3 className="text-base font-bold text-herbal-900 flex items-center gap-2 mb-2 uppercase tracking-wide">
                      <Leaf className="h-4 w-4 text-turmeric-500" />
                      Key Ingredients
                    </h3>
                    <div className="bg-earth-50 p-4 rounded-xl border border-earth-200">
                       <p className="text-sm font-medium text-herbal-800 leading-relaxed">
                         {product.ingredients}
                       </p>
                    </div>
                  </div>
                )}

                <div className="mt-6 text-sm text-gray-500 italic">
                  "Formulated with 100% natural Indonesian herbs (Jamu), certified for safety, authenticity, and efficacy."
                </div>
              </div>

              {/* Quantity and Price Calculation */}
              <div className="bg-earth-50 rounded-2xl p-6 mb-8 border border-earth-100">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
                  <div>
                    <span className="block text-sm font-bold text-gray-500 mb-2">Quantity</span>
                    <div className="flex items-center bg-white rounded-lg shadow-sm border border-gray-200">
                      <button 
                        onClick={handleDecrement}
                        className="p-3 text-gray-500 hover:text-herbal-800 hover:bg-gray-50 rounded-l-lg transition-colors"
                        disabled={quantity <= 1}
                      >
                        <Minus className="h-5 w-5" />
                      </button>
                      <span className="w-12 text-center font-bold text-lg text-herbal-900">{quantity}</span>
                      <button 
                        onClick={handleIncrement}
                        className="p-3 text-gray-500 hover:text-herbal-800 hover:bg-gray-50 rounded-r-lg transition-colors"
                      >
                        <Plus className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="text-center sm:text-right">
                    <span className="block text-sm font-bold text-gray-500 mb-1">Total Price</span>
                    <span className="text-3xl font-bold text-herbal-900">{formattedPrice}</span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 mb-10">
                <button 
                  onClick={handleAddToCart}
                  className="flex-1 bg-herbal-800 hover:bg-herbal-900 text-white font-bold py-4 px-6 rounded-xl flex items-center justify-center transition-all shadow-md hover:shadow-lg transform hover:-translate-y-1"
                >
                  <ShoppingCart className="mr-2 h-5 w-5" />
                  Add to Cart
                </button>
                <a 
                  href={`tel:${CONTACT_INFO.phone}`}
                  className="flex-1 bg-white border-2 border-herbal-800 text-herbal-800 font-bold py-4 px-6 rounded-xl flex items-center justify-center hover:bg-herbal-50 transition-all"
                >
                  <Phone className="mr-2 h-5 w-5" />
                  Call to Order
                </a>
              </div>

              {/* Trust Badges */}
              <div className="grid grid-cols-2 gap-y-4 gap-x-8 text-sm text-gray-500">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-turmeric-500 flex-shrink-0" />
                  <span className="font-medium">GMP Certified</span>
                </div>
                <div className="flex items-center gap-3">
                  <Truck className="h-5 w-5 text-turmeric-500 flex-shrink-0" />
                  <span className="font-medium">Fast Delivery</span>
                </div>
                <div className="flex items-center gap-3">
                  <ShieldCheck className="h-5 w-5 text-turmeric-500 flex-shrink-0" />
                  <span className="font-medium">Secure Payment</span>
                </div>
                 <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-turmeric-500 flex-shrink-0" />
                  <span className="font-medium">100% Natural</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};